// 推廣歸因參數（ac / fc / pix / ttpix / kpix）的解析與雙媒介落地 —— TI-15642 / JT-1785738540。
// 規格：spec/JT-1785738540_referral-param-persistence-fallback.md
//
// 職責邊界：
//  - 本檔只有純函數，所有 I/O（localStorage / cookie）由呼叫端注入，方便單元測試，
//    也讓 resolver 不假設儲存格式（localStorage 走 utils/storage.js 的 { value, expiry } 信封、
//    cookie 走裸字串）。
//  - 與 utils/conversionCookies.js 的 conv_ac（30 天、S2S 轉換用）是**兩條獨立管線**，
//    語意不同（此處 7 天、供註冊代理線歸因），不共用 key、不合併。
//
// 契約（三專案 client-pwa / client-pwa-ssr / client-pwa-cash 必須一致，見 spec §4）：
//  - 讀取順序：網址 → localStorage → cookie；同層內依 keys 陣列順序（ac 先於 referralCode），
//    且逐層跑完所有 key 再進下一層（網址的別名要勝過儲存的主 key，才符合 last-click）。
//  - 寫入：localStorage 與 cookie 雙寫，key 同名、maxAge 7 天，寫入後回讀驗證。
//  - 空值語意：擷取情境略過不寫；使用者主動清空（allowClear）則兩個媒介一起清除。

export const REFERRAL_MAX_AGE = 60 * 60 * 24 * 7

export const AC_KEYS = ['ac', 'referralCode']
export const FC_KEYS = ['fc', 'affiliate']
// Meta / 快手 / TikTok Pixel ID 皆無別名，但走同一套三層解析＋雙寫。
// 平台版三者的讀取端都在 repo 內（07/08 plugin 與 useCommon），補 cookie 層零外部相依。
export const PIX_KEYS = ['pix']
export const KPIX_KEYS = ['kpix']
export const TTPIX_KEYS = ['ttpix']

// 歷史髒值：這些字串真的被序列化後寫進過使用者端（useAuth.js 早有 ['undefined','null'] 的防呆）
export const DIRTY_VALUES = ['undefined', 'null', 'NaN']

export function normalizeReferral(value) {
  if (value === undefined || value === null) return null
  const trimmed = String(value).trim()
  if (trimmed === '' || DIRTY_VALUES.indexOf(trimmed) !== -1) return null
  return trimmed
}

export function searchOf(input) {
  if (!input) return ''
  // 先砍掉 #fragment：router 的 fullPath 是 path+query+hash，`?ac=meta2#promo` 不剝會解出
  // 'meta2#promo' 並靜默污染最後一個 query value（同 utils/conversionPayload.js parseAttribution 的既有處理）
  const text = String(input).split('#')[0]
  const index = text.indexOf('?')
  // 找不到 '?' 時回空字串，不要把整條 path 當 query 餵進 URLSearchParams
  if (index < 0) return ''
  // Telegram Mini App 會把分隔用的 & 誤編碼成 &amp%3B，不還原則第一個之後的參數全部解不出來。
  // 這裡無條件還原而非以 window.Telegram 判斷：telegram-web-app.js 是 async 載入，本模組的
  // 呼叫端（plugin init）執行時未必就緒；而 `&amp%3B` 在正常網址不會是合法字面值。
  return text.slice(index).replace(/&amp%3B/g, '&')
}

export function readReferral(io, keys) {
  const source = io || {}
  const params = new URLSearchParams(searchOf(source.search))

  for (let i = 0; i < keys.length; i++) {
    // 重複參數（`?ac=first&ac=second`）取第一個「有效」值：改版前 query 被解析成陣列、
    // `typeof value === 'string'` 判斷失敗而整組不儲存；空字串在前（`?ac=&ac=meta2`）也不得吃掉後面的有效值
    const value = pickFirstValid(params, keys[i])
    if (value) return { value, from: `url:${keys[i]}` }
  }
  for (let i = 0; i < keys.length; i++) {
    const value = normalizeReferral(readSafely(source.storageGet, keys[i]))
    if (value) return { value, from: `local:${keys[i]}` }
  }
  for (let i = 0; i < keys.length; i++) {
    const value = normalizeReferral(readSafely(source.cookieGet, keys[i]))
    if (value) return { value, from: `cookie:${keys[i]}` }
  }
  return { value: null, from: 'none' }
}

export function persistReferral(name, value, io, options) {
  const source = io || {}
  const allowClear = !!(options && options.allowClear)
  const normalized = normalizeReferral(value)

  if (normalized === null) {
    // 擷取情境解析為無值 → 不動既有副本（BR-5）；只有使用者主動清空欄位才連帶清掉兩個媒介
    if (!allowClear) return { local: 'skipped', cookie: 'skipped', skipped: true }
    const cleared = { local: 'ok', cookie: 'ok', cleared: true }
    if (!runSafely(source.storageRemove, name)) cleared.local = 'throw'
    if (!runSafely(source.cookieRemove, name)) cleared.cookie = 'throw'
    return cleared
  }

  const result = { local: 'ok', cookie: 'ok' }
  const writeOptions = { path: '/', maxAge: REFERRAL_MAX_AGE }

  if (!runSafely(source.storageSet, name, normalized, writeOptions)) result.local = 'throw'
  // 回讀驗證：iOS 受限 WebView 會「不 throw 但重讀為空」，只靠 try/catch 抓不到
  if (result.local === 'ok' && normalizeReferral(readSafely(source.storageGet, name)) !== normalized) {
    result.local = 'mismatch'
  }

  if (!runSafely(source.cookieSet, name, normalized, writeOptions)) result.cookie = 'throw'
  if (result.cookie === 'ok' && normalizeReferral(readSafely(source.cookieGet, name)) !== normalized) {
    result.cookie = 'mismatch'
  }

  return result
}

export function persistFailed(result) {
  if (!result || result.skipped) return false
  return result.local !== 'ok' || result.cookie !== 'ok'
}

function pickFirstValid(params, key) {
  const values = params.getAll(key)
  for (let i = 0; i < values.length; i++) {
    const value = normalizeReferral(values[i])
    if (value) return value
  }
  return null
}

function readSafely(fn, name) {
  if (typeof fn !== 'function') return null
  try {
    return fn(name)
  } catch (error) {
    return null
  }
}

function runSafely(fn, a, b, c) {
  if (typeof fn !== 'function') return false
  try {
    fn(a, b, c)
    return true
  } catch (error) {
    return false
  }
}
